import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getArticleBySlug, getRelatedArticles, getHotArticles } from '@/mocks/articles';
import { getArticleImage } from '@/mocks/images';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const hotArticles = getHotArticles();

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-foreground-600 mb-6">该文章已被删除或链接无效</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = getCategoryName(article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: getArticleImage(article.slug, 'featured'),
    url: `https://baoliaowang.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '爆料网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />

      <main>
        {/* Article Header */}
        <article className="pt-20">
          <header className="px-6 md:px-10 py-8 bg-background-100/60">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    {categoryName}
                  </Link>
                  {article.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-6 md:px-10 mt-8">
            <div className="rounded-lg overflow-hidden">
              <img
                src={getArticleImage(article.slug, 'featured')}
                alt={article.title}
                title={`${article.title} - 爆料网`}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-10">
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors">
                <i className="ri-weibo-line text-foreground-700"></i>
              </button>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors">
                <i className="ri-wechat-line text-foreground-700"></i>
              </button>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors">
                <i className="ri-share-forward-line text-foreground-700"></i>
              </button>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100/60">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard
                    key={rel.slug}
                    slug={rel.slug}
                    title={rel.title}
                    excerpt={rel.excerpt}
                    category={rel.category}
                    categoryName={getCategoryName(rel.category)}
                    imageUrl={getArticleImage(rel.slug, 'card')}
                    author={rel.author}
                    date={rel.date}
                    views={rel.views}
                    tags={rel.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Hot sidebar section */}
        <section className="py-12 md:py-16 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <i className="ri-fire-fill text-accent-500"></i>
              热门爆料
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {hotArticles.map((hot) => (
                <ArticleCard
                  key={hot.slug}
                  slug={hot.slug}
                  title={hot.title}
                  excerpt={hot.excerpt}
                  category={hot.category}
                  categoryName={getCategoryName(hot.category)}
                  imageUrl={getArticleImage(hot.slug, 'compact')}
                  author={hot.author}
                  date={hot.date}
                  views={hot.views}
                  tags={hot.tags}
                  variant="compact"
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}